import { useState } from 'react';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import ErrorMessage from '../../../components/ErrorMessage';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import authService from '../../../services/useCases/auth';
import { formatError, snackbarUtils } from '../../../utils';

import Head from '../../../components/Head';
import Card from '../../../components/Auth/Card';
import AuthHeader from '../../../components/Auth/Header';
import BackToLogin from '../../../components/Auth/BackToLogin';
import AuthFooter from '../../../components/Auth/Footer';
import { AtSignIcon, LockIcon, AssignmentIndIcon } from '../../../assets/icons';
import useAuthStyles from '../../../styles/pages/auth';
import useStyles from './styles';

interface Values {
  gpid: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email é obrigatório')
    .email('Digite um email válido'),
  password: Yup.string().required('Senha é obrigatória'),
});

export default function Login() {
  const classes = useStyles();
  const authClasses = useAuthStyles();

  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  const handleFormik = async (
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) => {
    try {
      // console.log('values', values)
      await authService.register(values);
      formikHelpers.resetForm({ values: {} as Values });
      setHasSuccess(true);
    } catch (error) {
      console.error('cadastro erro: ', error);
      snackbarUtils.error(formatError(error));
      setHasError(true);
    }
    formikHelpers.setSubmitting(false);
  };

  const handleClose = () => {
    setHasError(false);
    setHasSuccess(false);
  };

  return (
    <Container maxWidth="sm" style={{ zIndex: 2 }}>
      <Head>Cadastro</Head>
      <Formik
        initialValues={{} as Values}
        validationSchema={validationSchema}
        onSubmit={handleFormik}
      >
        {({ isSubmitting, values, handleChange, touched, errors }) => (
          <Form
            className={[authClasses.container, classes.container].join(' ')}
          >
            <Grid container className={authClasses.cardWrapper}>
              <AuthHeader outside />
              <Card>
                <CardContent>
                  <Grid
                    item
                    xs={12}
                    className={authClasses.descriptionContainer}
                  >
                    <Typography variant="h1">Cadastre-se</Typography>
                    <Typography>
                      Preencha todas as informações e conclua seu cadastro
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                    className={authClasses.inputsContainer}
                  >
                    <Grid item xs={12}>
                      <TextField
                        id="gpid"
                        name="gpid"
                        label="Matrícula"
                        value={values.gpid}
                        onChange={handleChange}
                        error={touched.gpid && Boolean(errors.gpid)}
                        helperText={touched.gpid && errors.gpid}
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <AssignmentIndIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="email"
                        name="email"
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <AtSignIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="password"
                        name="password"
                        label="Senha"
                        type="password"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <LockIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="confirmPassword"
                        name="confirmPassword"
                        label="Confirmar Senha"
                        type="password"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        size="small"
                        InputProps={{
                          className: authClasses.inputs,
                          endAdornment: (
                            <InputAdornment position="end">
                              <LockIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ErrorMessage
                        open={hasError || hasSuccess}
                        handleClose={handleClose}
                        title={hasError ? 'Erro' : 'Sucesso'}
                        severity={hasError ? 'error' : 'success'}
                        message={
                          hasError ? (
                            <>
                              Erro ao realizar o cadastro. Favor tente
                              novamente.
                            </>
                          ) : (
                            <>
                              Cadastro efetuado com sucesso. Volte e faça login.
                            </>
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        size="large"
                        disabled={isSubmitting}
                        className={authClasses.submitButton}
                      >
                        {isSubmitting ? 'Aguarde...' : 'Concluir cadastro'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <BackToLogin />
            </Grid>
            <Box mt={10}>
              <AuthFooter />
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
}
